<template>
  <div ref="videoEmbedRef" :style="innerStyle" class="video-embed__inner">
    <div v-if="src" :style="displayStyle" class="video-embed__display">
      <div
        v-if="src"
        class="video-embed__background"
        :style="{
          backgroundColor: backgroundColor
        }"
      ></div>

      <div
        class="video-embed__wrap embed-responsive embed-responsive-4by3"
        :style="{ opacity: opacity ? opacity / 100 : '0' }"
      >
        <video
          v-if="videoType && videoType.includes('external')"
          ref="externalVideo"
          class="external-video"
          :style="videoStyle"
        >
          <source :src="src" type="video/mp4" />
        </video>

        <div ref="videoPlayerRef" class="video-embed__element embed-responsive-item"></div>
      </div>

      <div v-if="src && !controls" class="video-embed__control-overlay"></div>
    </div>
  </div>
</template>

<script lang="ts">
import type { CSSProperties, PropType, Raw } from 'vue';
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import YouTubePlayerConstructor from 'youtube-player';
import type { YouTubePlayer } from 'youtube-player/dist/types';
import PlayerStates from 'youtube-player/dist/constants/PlayerStates';
import VimeoPlayer from '@vimeo/player';
import { getYouTubeVideoIdFromUrl, transformMeasurementToNumber } from '@/src/utilities/Utilities';
import useLeadScore from '@/src/hooks/useLeadscore';
import type { AlignContentType } from '@/src/typings/enums/enums';
import { VideoEmbedType, VideoFormatType } from '@/src/typings/enums/enums';
import useDevice from '@/src/hooks/useDevice';
import { useCampaignStore } from '@/src/store/campaign';

interface StateChangeEvent {
  data: number;
}

export default defineComponent({
  name: 'VideoComponent',
  props: {
    src: {
      type: String,
      required: true
    },
    videoUrl: {
      type: String
    },
    videoType: {
      type: String as PropType<VideoEmbedType>
    },
    width: {
      type: String
    },
    height: {
      type: String
    },

    alignment: {
      type: String as PropType<AlignContentType>
    },

    sound: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String
    },
    controls: {
      type: Boolean,
      default: false
    },
    loop: {
      type: Boolean,
      default: false
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    opacity: {
      type: Number,
      default: 100
    },
    modelId: {
      type: Number
    }
  },
  emits: ['ended'],
  setup(props, context) {
    const campaignStore = useCampaignStore();
    const campaignState = campaignStore.model?.state;
    const videoEmbedRef = ref<HTMLElement | null>(null);
    const videoPlayerRef = ref<Raw<HTMLElement> | null>(null);
    const externalVideo = ref<HTMLVideoElement | null>(null);
    const videoWidth = ref(0);
    const videoHeight = ref(0);
    const displayType = ref('inline-block');

    let firstplay = true;
    let player: YouTubePlayer | VimeoPlayer | null = null;

    const youtubePlayer = () => {
      let videoSrc = props.src;

      if (props.src.includes('shorts/')) {
        const [shortsUrl, params] = props.src.split('?');
        const videoId = shortsUrl.split('shorts/')[1];
        const queryParams = params ? `&${params}` : '';

        videoSrc = `https://www.youtube.com/watch?v=${videoId}${queryParams}`;
      }

      if (videoPlayerRef.value && videoSrc) {
        player = YouTubePlayerConstructor(videoPlayerRef.value, {
          videoId: getYouTubeVideoIdFromUrl(videoSrc),
          playerVars: {
            controls: props.controls ? 1 : 0,
            rel: 0,
            ...(props.controls && { modestbranding: 1 }),
            playsinline: 1
          }
        });

        player.on('ready', () => {
          if (!props.sound && player && 'mute' in player) {
            player.mute();
          }

          if (props.autoplay && player && 'playVideo' in player) {
            player.playVideo();
          }
        });

        player.on('stateChange', (event: StateChangeEvent) => {
          trackLeadScore();

          if (event.data == PlayerStates.ENDED) {
            context.emit('ended');
          }

          if (props.autoplay && event.data === PlayerStates.ENDED && player && 'playVideo' in player) {
            player.playVideo();
          }
        });
      }
    };

    const trackLeadScore = () => {
      if (firstplay && campaignState?.config?.leadScoreEnabled && !props.autoplay) {
        firstplay = false;
        useLeadScore().track({
          inSession: true,
          object: 'ADDON',
          object_value: `video-embed_${props.modelId}_play`,
          distinct: true
        });
      }
    };

    const vimeoPlayer = () => {
      if (props.src && videoPlayerRef.value) {
        player = new VimeoPlayer(videoPlayerRef.value, {
          url: props.src,
          loop: props.loop,
          autoplay: props.autoplay,
          controls: props.controls
        });

        player
          .ready()
          .then(() => {
            if (!props.sound && player && 'setVolume' in player) {
              player.setVolume(0);
            }

            if (props.autoplay && player && 'play' in player) {
              player.play();
            }
          })

          .catch(() => {});

        player.on('play', () => {
          trackLeadScore();
        });

        player.on('ended', () => {
          context.emit('ended');
        });
      }
    };

    const externalPlayer = () => {
      if (externalVideo.value && props.src?.includes('.mp4')) {
        externalVideo.value.addEventListener('playing', trackLeadScore);
        externalVideo.value.addEventListener('ended', () => {
          context.emit('ended');
        });

        if (props.autoplay) {
          externalVideo.value.playsInline = true;
          const promise = externalVideo.value?.play();
          if (promise !== undefined) {
            promise
              .then(() => {
                // Autoplay started!
              })
              .catch(() => {
                if (externalVideo.value) {
                  externalVideo.value.muted = true;
                  externalVideo.value.play();
                  // Autoplay was prevented.
                }
              });
          }
        }

        if (!props.sound) {
          externalVideo.value.muted = true;
        }
        if (props.loop) {
          externalVideo.value.loop = true;
        }
        if (props.controls) {
          externalVideo.value.controls = true;
        }
      }
    };

    const play = () => {
      if (externalVideo.value) {
        externalVideo.value.play();
      } else if (player) {
        if ('playVideo' in player) {
          player.playVideo();
        } else if ('play' in player) {
          player.play();
        }
      }
    };

    const convertVideoWidth = (offsetWidth: number, customWidth: string) => {
      const parsedCustomWidth = transformMeasurementToNumber(customWidth);
      if (props.width?.includes('px')) {
        return parsedCustomWidth;
      } else if (props.width?.includes('%')) {
        return Math.floor((offsetWidth * parsedCustomWidth) / 100);
      } else {
        return parsedCustomWidth;
      }
    };

    const displayStyle = computed<CSSProperties>(() => {
      return {
        display: displayType.value,
        width: videoWidth.value + 'px',
        height: videoHeight.value + 'px'
      };
    });

    const setSize = () => {
      const containerWidth = videoEmbedRef.value?.offsetWidth;
      const containerHeight = videoEmbedRef.value?.offsetHeight;

      const settingsWidth = props.width;
      const settingsHeight = props.height;
      let ratio = 16 / 9;

      // on re-render reset displayType
      displayType.value = 'inline-block';

      // set default values: *important* to handle it in component, because we have to calculate aspect ratio if either is empty
      if (!settingsWidth) {
        videoWidth.value = 400;
      }
      if (!settingsHeight) {
        videoHeight.value = 250;
      }
      // Both values are set, just parse it on.
      if (settingsWidth && settingsHeight) {
        videoHeight.value = parseFloat(settingsHeight);
        videoWidth.value = parseFloat(settingsHeight);
      }
      // set video width - it can be both px and percentage - if percentage we want a pixel value
      if (settingsWidth && props.width && containerWidth) {
        videoWidth.value = convertVideoWidth(containerWidth, props.width);
      } else if (settingsHeight && containerHeight) {
        videoHeight.value = (containerHeight * parseFloat(settingsHeight)) / 100;
      }

      if (settingsWidth && !settingsHeight) {
        videoHeight.value = videoWidth.value / ratio;
      } else if (settingsHeight && !settingsWidth) {
        videoHeight.value = parseFloat(settingsHeight);
        videoWidth.value = parseFloat(settingsHeight) * ratio;
      }
      // finally check if there is space for the width set earlier
      if (containerWidth && containerWidth < videoWidth.value) {
        ratio = videoWidth.value / videoHeight.value;
        displayType.value = 'block';
        videoWidth.value = containerWidth;
        videoHeight.value = containerWidth / ratio;
      }
    };

    const videoStyle = computed<CSSProperties>(() => {
      return {
        width: `${videoWidth.value}px`
      };
    });

    const innerStyle = computed<CSSProperties>(() => {
      return {
        textAlign: props.alignment ?? 'center',
        width: '100%'
      };
    });

    const resizer = new ResizeObserver(() => {
      setSize();
    });

    const initVideoEmbed = () => {
      setSize();

      const videoUrl = props.src;
      if (videoUrl?.includes(VideoFormatType.YOUTUBE)) {
        youtubePlayer();
      }
      if (videoUrl?.includes(VideoFormatType.VIMEO)) {
        vimeoPlayer();
      }
      if (props.videoType === VideoEmbedType.EXTERNAL) {
        externalPlayer();
      }

      if (videoEmbedRef.value instanceof HTMLElement) {
        resizer.observe(videoEmbedRef.value);
      }
    };

    watch(
      () => [props.videoUrl, useDevice()],
      () => {
        if (!props.videoUrl || player !== null || (player && 'destroy' in player)) {
          if (player) {
            player.destroy();
            player = null;
          }
        }

        nextTick(() => {
          if (props.videoUrl && !player) {
            initVideoEmbed();
          }
        });
      }
    );

    watch(
      () => [props.width, props.height],
      () => {
        setSize();
      },
      { immediate: true }
    );

    onMounted(initVideoEmbed);

    onBeforeUnmount(() => {
      if (externalVideo.value) {
        externalVideo.value.removeEventListener('playing', trackLeadScore);
      }

      resizer.disconnect();
    });

    return {
      videoWidth,
      videoHeight,
      videoEmbedRef,
      videoPlayerRef,
      externalVideo,
      displayStyle,
      videoStyle,
      innerStyle,
      play
    };
  }
});
</script>

<style lang="scss">
.video-embed {
  display: block;

  &__wrap {
    z-index: 1;
    display: block;
    width: 100%;
    height: 100%;
  }

  &__display {
    position: relative;
  }

  &__control-overlay {
    z-index: 0;
  }

  &__background,
  &__control-overlay {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }

  .external-video {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: auto;
  }
}
</style>
